import { Component, ViewChild, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { AuthService } from '../../services/auth.service';
import { AuthShellComponent } from '../auth-shell/auth-shell.component';
import { CountryPhoneInputComponent } from '../country-phone-input/country-phone-input.component';
import { OtpInputComponent } from '../otp-input/otp-input.component';
import { ROUTES } from '../../../core/constants/routes.constants';
import { environment } from '../../../../environments/environment';

type LoginMode = 'password' | 'otp';

const OTP_LENGTH = 6;

@Component({
  selector: 'app-login',
  imports: [FormsModule, RouterLink, AuthShellComponent, CountryPhoneInputComponent, OtpInputComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);

  @ViewChild(OtpInputComponent) private otpInput?: OtpInputComponent;

  protected readonly routes = ROUTES;
  protected readonly isDummyMode = environment.dummy;

  protected readonly loginMode = signal<LoginMode>('password');

  protected readonly email = signal('');
  protected readonly password = signal('');
  protected readonly rememberMe = signal(true);
  protected readonly passwordVisible = signal(false);
  protected readonly loading = signal(false);
  protected readonly errorMessage = signal('');

  protected readonly otpCountry = signal('India');
  protected readonly otpPhone = signal('');
  protected readonly otpCode = signal('');
  protected readonly otpSent = signal(false);
  protected readonly otpSending = signal(false);
  protected readonly otpVerifying = signal(false);
  protected readonly otpError = signal('');

  protected readonly otpButtonLabel = computed(() => {
    if (!this.otpSent()) return this.otpSending() ? 'Sending OTP…' : 'Send OTP';
    return this.otpVerifying() ? 'Verifying…' : 'Login';
  });

  protected readonly otpSubmitDisabled = computed(() => {
    if (!this.otpSent()) return this.otpSending() || !this.otpPhone();
    return this.otpVerifying() || this.otpCode().length !== OTP_LENGTH;
  });

  setLoginMode(mode: LoginMode): void {
    this.loginMode.set(mode);
    this.errorMessage.set('');
    if (mode === 'password') {
      this.resetOtpFlow();
    }
  }

  togglePasswordVisibility(): void {
    this.passwordVisible.update((visible) => !visible);
  }

  handleSubmit(): void {
    if (this.loginMode() === 'password') {
      this.onSubmit();
    } else if (!this.otpSent()) {
      this.sendOtp();
    } else {
      this.verifyOtp();
    }
  }

  onSubmit(): void {
    this.errorMessage.set('');
    this.loading.set(true);

    this.authService.login({ email: this.email(), password: this.password() }).subscribe({
      next: () => {
        this.loading.set(false);
        this.navigateAfterLogin();
      },
      error: () => {
        this.loading.set(false);
        this.errorMessage.set('Invalid email or password. Please try again.');
      }
    });
  }

  sendOtp(): void {
    this.otpError.set('');
    this.otpSending.set(true);

    this.authService.sendOtp(this.otpPhone()).subscribe({
      next: () => {
        this.otpSending.set(false);
        this.otpSent.set(true);
        setTimeout(() => this.otpInput?.start());
      },
      error: () => {
        this.otpSending.set(false);
        this.otpError.set('Could not send OTP. Please try again.');
      }
    });
  }

  verifyOtp(): void {
    this.otpError.set('');
    this.otpVerifying.set(true);

    this.authService.verifyOtp({ phoneNumber: this.otpPhone(), code: this.otpCode() }).subscribe({
      next: () => {
        this.otpVerifying.set(false);
        this.navigateAfterLogin();
      },
      error: () => {
        this.otpVerifying.set(false);
        this.otpError.set('Invalid OTP. Please try again.');
      }
    });
  }

  private resetOtpFlow(): void {
    this.otpSent.set(false);
    this.otpCode.set('');
    this.otpError.set('');
  }

  private navigateAfterLogin(): void {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || ROUTES.DASHBOARD;
    this.router.navigateByUrl(returnUrl);
  }
}
